import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, LogOut, Send, RefreshCw, Lock } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "admin_token";
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).join(" ");
  return "Something went wrong. Please try again.";
};
const inputCls = "w-full rounded-md border border-[#D8CFF0] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#6618D0]";

function Login({ onLogin }) {
  const [passcode, setPasscode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passcode });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="mx-auto mt-24 max-w-sm rounded-2xl bg-white p-8 shadow-lg">
      <Lock className="h-8 w-8 text-[#6618D0]" />
      <h1 className="mt-4 font-serif-display text-2xl font-bold text-[#6618D0]">Owner area</h1>
      <p className="mt-1 text-sm text-[#5a5a5a]">Enter your admin passcode.</p>
      <input data-testid="admin-passcode-input" type="password" value={passcode} onChange={(e) => setPasscode(e.target.value)} className={`${inputCls} mt-5`} placeholder="Passcode" autoFocus />
      <button data-testid="admin-login-button" disabled={busy || !passcode} className="mt-4 w-full rounded-md bg-[#6618D0] py-3 text-sm font-bold text-white transition-colors hover:bg-[#5512b0] disabled:opacity-60">
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}

function Stat({ label, value, id }) {
  return (
    <div className="rounded-xl bg-white p-5 shadow-sm">
      <div className="text-xs font-bold tracking-wide text-[#7a7a7a]">{label}</div>
      <div data-testid={`admin-stat-${id}`} className="mt-1 font-serif-display text-2xl font-bold text-[#6618D0]">{value}</div>
    </div>
  );
}

const STATUS_STYLE = { paid: "bg-green-100 text-green-800", pending: "bg-amber-100 text-amber-800" };

function OrdersTable({ orders }) {
  if (!orders.length) return <p data-testid="admin-orders-empty" className="p-6 text-sm text-[#7a7a7a]">No orders yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table data-testid="admin-orders-table" className="w-full min-w-[820px] text-left text-sm">
        <thead className="bg-[#F3EEFC] text-xs uppercase tracking-wide text-[#6618D0]">
          <tr>{["Date", "Status", "Buyer", "Item", "Qty", "Total", "Ships to", "Invite"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.session_id} data-testid="admin-order-row" className="border-t border-[#eee] align-top">
              <td className="px-4 py-3 whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
              <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_STYLE[o.payment_status] || "bg-gray-100 text-gray-700"}`}>{o.payment_status}</span></td>
              <td className="px-4 py-3"><div className="font-semibold">{o.customer_name}</div><div className="text-xs text-[#7a7a7a]">{o.customer_email}</div></td>
              <td className="px-4 py-3">{o.option === "bundle" ? "Book + Workbook" : "Book"}</td>
              <td className="px-4 py-3">{o.quantity}</td>
              <td className="px-4 py-3">{money(o.amount_total)}</td>
              <td className="px-4 py-3 text-xs">{[o.shipping_name, o.line1, o.line2, [o.city, o.state, o.postal_code].filter(Boolean).join(" "), o.country].filter(Boolean).join(", ") || "—"}</td>
              <td className="px-4 py-3 text-xs">{o.livestream_email_status === "failed" ? <span className="font-bold text-red-600">Failed</span> : o.livestream_email_status === "queued" ? "Sending…" : o.livestream_sent_at ? "Sent" : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LivestreamPanel({ auth, stats, onSent }) {
  const [ls, setLs] = useState(null);
  const [confirm, setConfirm] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/livestream`, auth).then(({ data }) => setLs(data))
      .catch(() => setLs({ title: "Private Launch Day Livestream", when: "", join_url: "", notes: "" }));
  }, [auth]);
  if (!ls) return <section className="rounded-2xl bg-white p-6 text-sm text-[#7a7a7a] shadow-sm">Loading livestream details…</section>;
  const set = (k) => (e) => setLs({ ...ls, [k]: e.target.value });
  const save = async () => {
    try {
      await axios.put(`${API}/admin/livestream`, ls, auth);
      toast.success("Livestream details saved");
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const send = async (resendAll) => {
    if (!(await save())) return;
    try {
      const { data } = await axios.post(`${API}/admin/livestream/send`, { resend_all: resendAll }, auth);
      toast.success(`Sending ${data.queued} invite${data.queued === 1 ? "" : "s"}${data.queued > 1 ? `, about ${Math.max(1, Math.ceil(data.estimated_minutes))} min. It keeps going even if you close this page` : ""}`);
      setTimeout(onSent, 3000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const notInvited = stats.paid_orders - stats.invited - (stats.sending || 0);
  return (
    <section data-testid="admin-livestream-panel" className="rounded-2xl bg-white p-6 shadow-sm">
      <h2 className="font-serif-display text-xl font-bold text-[#6618D0]">Launch day livestream invite</h2>
      <p className="mt-1 text-sm text-[#5a5a5a]">Buyers get an email with a button to their own private page that shows these details.</p>
      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="text-xs font-bold text-[#5a5a5a]">Title<input data-testid="ls-title-input" className={`${inputCls} mt-1`} value={ls.title} onChange={set("title")} /></label>
        <label className="text-xs font-bold text-[#5a5a5a]">Date and time<input data-testid="ls-when-input" className={`${inputCls} mt-1`} value={ls.when} onChange={set("when")} placeholder="Tuesday, Oct 27 at 7pm MT" /></label>
        <label className="text-xs font-bold text-[#5a5a5a] md:col-span-2">Join link (Zoom, YouTube…)<input data-testid="ls-url-input" className={`${inputCls} mt-1`} value={ls.join_url} onChange={set("join_url")} placeholder="https://" /></label>
        <label className="text-xs font-bold text-[#5a5a5a] md:col-span-2">Note for buyers (optional)<textarea data-testid="ls-notes-input" rows={2} className={`${inputCls} mt-1`} value={ls.notes} onChange={set("notes")} /></label>
      </div>
      <div className="mt-5 flex flex-wrap gap-3">
        <button data-testid="ls-save-button" onClick={save} className="rounded-md border border-[#6618D0] px-5 py-2.5 text-sm font-bold text-[#6618D0] hover:bg-[#F3EEFC]">Save details</button>
        <button data-testid="ls-send-new-button" onClick={() => setConfirm("new")} disabled={!notInvited} className="inline-flex items-center gap-2 rounded-md bg-[#6618D0] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#5512b0] disabled:opacity-50">
          <Send className="h-4 w-4" /> Send to {notInvited} buyer{notInvited === 1 ? "" : "s"} not yet invited
        </button>
        <button data-testid="ls-resend-all-button" onClick={() => setConfirm("all")} disabled={!stats.paid_orders} className="inline-flex items-center gap-2 rounded-md bg-[#990EE5] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#7f0bbf] disabled:opacity-50">
          <RefreshCw className="h-4 w-4" /> Resend to all {stats.paid_orders} paid buyers
        </button>
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="ls-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send real emails now?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "all"
                ? `This emails all ${stats.paid_orders} paid buyers, including those already invited.`
                : `This emails ${notInvited} paid buyer${notInvited === 1 ? " who hasn't" : "s who haven't"} been invited yet.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ls-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="ls-confirm-send" onClick={() => send(confirm === "all")}>Send invites</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const auth = React.useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const load = useCallback(() => {
    axios.get(`${API}/admin/orders`, auth).then(({ data }) => setData(data)).catch((e) => {
      if (e?.response?.status === 401) onLogout();
      else toast.error(errMsg(e));
    });
  }, [auth, onLogout]);
  useEffect(load, [load]);
  const sending = data?.orders.some((o) => o.livestream_email_status === "queued");
  useEffect(() => {
    if (!sending) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [sending, load]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders.csv`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `preorders-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the spreadsheet");
    }
  };

  if (!data) return <p className="p-10 text-sm text-[#7a7a7a]">Loading orders…</p>;
  const s = data.stats;
  return (
    <div className="mx-auto max-w-7xl space-y-6 px-5 py-10 md:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif-display text-3xl font-bold text-[#6618D0]">Preorders</h1>
        <div className="flex gap-2">
          <button data-testid="admin-export-csv-button" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-md bg-[#6618D0] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#5512b0]"><Download className="h-4 w-4" /> Download spreadsheet</button>
          <button data-testid="admin-logout-button" onClick={onLogout} className="inline-flex items-center gap-2 rounded-md border border-[#D8CFF0] bg-white px-4 py-2.5 text-sm font-bold text-[#5a5a5a]"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat id="paid" label="PAID ORDERS" value={s.paid_orders} />
        <Stat id="copies" label="COPIES" value={s.copies} />
        <Stat id="revenue" label="REVENUE" value={money(s.revenue)} />
        <Stat id="invited" label="INVITED TO LIVESTREAM" value={`${s.invited}/${s.paid_orders}`} />
      </div>
      <LivestreamPanel auth={auth} stats={s} onSent={load} />
      <section className="overflow-hidden rounded-2xl bg-white shadow-sm"><OrdersTable orders={data.orders} /></section>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);
  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#EAE9F6]">
      {token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />}
    </div>
  );
}
